import Wizard from '@service-victoria/component-library/components/Wizard';
import Props from '@service-victoria/component-library/props';
import { BroadcastChannel } from 'broadcast-channel';
import { FormikErrors, FormikTouched } from 'formik';
import * as React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { v4 as uuid } from 'uuid';

import { app as appContext } from 'app/context';
import * as Interfaces from 'app/interfaces';
import * as Steps from 'app/transactions/provider/steps';
import { constants, logger } from 'app/utils';
import { TransactionType } from 'app/interfaces/common';
import { useActivityProvider } from 'app/hooks';
import { userManager } from 'app/services';

const channel = new BroadcastChannel<
  Pick<Interfaces.Store.State['transactions'], TransactionType.ProviderDetails>
>(constants.PROVIDER_BROADCAST_CHANNEL_KEY, {
  webWorkerSupport: false
});

const Transaction: React.FC = () => {
  // The wizard was created with "hash" url support, so we need to extract its value to know at what step the user was
  const location = useLocation();
  const history = useHistory();
  const context = appContext.useAppContext();
  const { providerDetails } = useActivityProvider();
  const { hash } = location;

  React.useEffect(() => {
    const onTransactionComplete = (
      event: Pick<Interfaces.Store.State['transactions'], TransactionType.ProviderDetails>
    ) => {
      if (
        event[TransactionType.ProviderDetails].ID ===
        context.currentState.transactions[TransactionType.ProviderDetails].ID
      ) {
        const newTransactionId = uuid();
        // Another tab has completed with the same transaction ID. Set a new one.
        context.actions.setTransactionID(newTransactionId, TransactionType.ProviderDetails);
      }
    };

    channel.addEventListener('message', onTransactionComplete);
    return () => {
      channel.removeEventListener('message', onTransactionComplete);
    };
  }, [context.actions, context.currentState.transactions]);

  // Get the current hash value of the url to set initialStep index (to load the user in same step in case the screen got refershed in middle of the process)
  // const initialStepId = hash.substr(1) ? hash.substr(1) : Steps.Step1.Step.id;
  const initialStepId = hash.substr(1) ? hash.substr(1) : Steps.ProviderDetails.Step.id;

  const steps: Props.Wizard.Step<any>[] = [
    // Provider details
    Steps.ProviderDetails.Step,
    // Review
    Steps.Review.Step,
    // Done
    Steps.Done.Step,
    // The below Error step will not appear in form stepper, as it shares the same step name as the Done step
    Steps.Error.Step
  ];

  const emptyProviderFormValues: Partial<Interfaces.Common.ProviderFormValues> = {};

  const initialValues = {
    providerDetails: providerDetails
      ? {
          providerName: providerDetails?.organisation.name,
          activityType: providerDetails?.organisation.activityType,
          providerPhone: providerDetails?.organisation.phone,
          address: {
            summaryData: {},
            fullDetails: {
              fullAddress: providerDetails?.organisation.address.street,
              postCode: providerDetails?.organisation.address.postcode,
              state: providerDetails?.organisation.address.state,
              suburb: providerDetails?.organisation.address.city
            }
          },
          role: providerDetails?.authorisedContact.role
        }
      : emptyProviderFormValues?.providerDetails
  };

  const initialErrors: FormikErrors<Interfaces.Common.ProviderFormValues> =
    Steps.ProviderDetails.initialErrors;

  const initialTouched: FormikTouched<Interfaces.Common.ProviderFormValues> =
    Steps.ProviderDetails.initialTouched;

  const validationSchema = Yup.object<Interfaces.Common.ProviderFormValues>().shape({
    providerDetails: Steps.ProviderDetails.providerDetailsSchema
  });

  const initialAppStates: Props.Wizard.AppContextStates = {
    [constants.ProviderStorageKeys.State]: context
  };

  React.useEffect(() => {
    logger.setContext('transactionId', context.currentState.transactionID);
  }, [context.currentState.transactionID]);

  return (
    <>
      {!initialValues.providerDetails && history.push(constants.Routes.AccountCallback)}
      {initialValues.providerDetails && (
        <Wizard
          headerProps={{
            ...constants.headerProps,
            ...userManager.signinHeaderProps
          }}
          initialValues={initialValues}
          initialTouched={initialTouched}
          initialErrors={initialErrors}
          initialAppStates={initialAppStates}
          validationSchema={validationSchema}
          steps={steps}
          initialStepId={initialStepId}
          breadCrumbs={constants.breadCrumbs}
          onSubmit={() => {
            const { ID, startTs } =
              context.currentState.transactions[TransactionType.ProviderDetails];

            if (!channel.isClosed) {
              channel.postMessage({
                [TransactionType.ProviderDetails]: {
                  ID,
                  startTs
                }
              });
              channel.close();
            }

            logger.info('Complete transaction', {
              transactionID: ID,
              executionTime: startTs ? +new Date() - startTs : 0
            });
          }}
          isStepperEnabed
          isHashEnabled
          showFooter
          scrollOnSubmitError
        />
      )}
    </>
  );
};

export default Transaction;
